import { RawDraftContentState } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { isIOS, isMobile } from 'react-device-detect';
import ReactPlayer from 'react-player';
import { TrackProps } from 'react-player/file';

import Icone from 'Component/Icone';
import OffCanvas from 'Component/OffCanvas';
import UnidadeDeAprendizagem from 'Interface/UnidadesDeAprendizagem';
import { alternarInterface } from 'Reducer/Config';
import { useAppDispatch } from 'Store';

import Bibliografias from './Bibliografias';
import DicasMais from './DicasMais';
import Materiais from './Materiais';
import Quiz from './Quiz';

interface Props {
  titulo?: string;
  aoFechar?: () => void;
  url: string;
  livro?: string;
  unidadeDeAprendizagem?: UnidadeDeAprendizagem;
}

const Video = ({
  titulo,
  aoFechar,
  url,
  livro,
  unidadeDeAprendizagem,
}: Props): React.ReactElement => {
  const dispatch = useAppDispatch();
  const player = useRef<ReactPlayer>(null);
  const [controles, setControles] = useState<boolean>(false);
  const [offcanvas, setOffcanvas] = useState<boolean>(false);
  const [aba, setAba] = useState<number>(0);
  const [porcentagemLinhaDoTempo, setPorcentagemLinhaDoTempo] =
    useState<number>(0);
  const [config, setConfig] = useState({
    segundosTocados: 0,
    segundosCarregados: 100,
    telaCheia: false,
    tocando: true,
    mudo: true,
  });

  useEffect(() => {
    setControles(true);
    setTimeout(() => setControles(false), 5000);

    dispatch(alternarInterface());

    return () => {
      dispatch(alternarInterface());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const internalPlayer =
    player.current?.getInternalPlayer() as HTMLVideoElement;

  const telaCheia = () => {
    if (!config.telaCheia) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const elemento = document.getElementById('player') as any;

      if (elemento.requestFullscreen) {
        elemento.requestFullscreen();
      } else if (elemento.webkitRequestFullscreen) {
        elemento.webkitRequestFullscreen();
      } else if (elemento.msRequestFullscreen) {
        elemento.msRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
      } else if (document.webkitExitFullscreen) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        document.webkitExitFullscreen();
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
      } else if (document.msExitFullscreen) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        document.msExitFullscreen();
      }
    }

    setConfig({
      ...config,
      telaCheia: !config.telaCheia,
    });
  };

  const tocar = () => {
    setConfig({ ...config, tocando: !config.tocando });
  };

  const mutar = () => {
    setConfig({ ...config, mudo: !config.mudo });
  };

  const progresso = (evento: {
    playedSeconds: number;
    loadedSeconds: number;
  }) => {
    setConfig({
      ...config,
      segundosTocados: evento.playedSeconds,
      segundosCarregados: evento.loadedSeconds,
    });
  };

  const mouseSaiuDaLinhaDoTempo = () => {
    setPorcentagemLinhaDoTempo(0);
  };

  const larguraBarra = document.getElementById('player')?.offsetWidth || 0;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mouseSobreLinhaDoTempo = (evento: any) => {
    const largura = larguraBarra;

    const porcentagem = (evento.nativeEvent.offsetX * 100) / largura;

    setPorcentagemLinhaDoTempo(porcentagem);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cliqueSobreLinhaDoTempo = (evento: any) => {
    const largura = larguraBarra;

    const porcentagem = (evento.nativeEvent.offsetX * 100) / largura / 100;

    setConfig({
      ...config,
      segundosTocados: (player.current?.getDuration() || 1) * porcentagem,
    });

    player.current?.seekTo(porcentagem);
  };

  const avancar10 = () => {
    const segundos = config.segundosTocados + 10;

    setConfig({
      ...config,
      segundosTocados: segundos,
    });

    player.current?.seekTo(segundos, 'seconds');
  };

  const voltar10 = () => {
    const segundos = config.segundosTocados - 10;

    setConfig({
      ...config,
      segundosTocados: segundos,
    });

    player.current?.seekTo(segundos, 'seconds');
  };

  const tracks = useMemo(() => {
    const tracks: TrackProps[] = [];

    unidadeDeAprendizagem?.legendas?.forEach((legenda) => {
      tracks.push({
        label: legenda.nome,
        kind: 'subtitles',
        src: legenda.url,
        srcLang: legenda.codigo,
      });
    });

    return tracks;
  }, [unidadeDeAprendizagem?.legendas]);

  const textTracks: TextTrack[] = useMemo(() => {
    const textTracks = [];
    const tracks = internalPlayer?.textTracks;

    if (tracks) {
      for (const track of tracks) {
        if (track.kind === 'subtitles') {
          textTracks.push(track);
        }
      }
    }

    return textTracks;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [internalPlayer?.textTracks.length]);

  const toggleTrack = (track: TextTrack): void => {
    if (track.mode === 'showing') {
      track.mode = 'disabled';
    } else {
      track.mode = 'showing';
    }
  };

  return (
    <div
      id="player"
      className="bg-black position-absolute top-0 bottom-0 start-0 end-0"
    >
      <ReactPlayer
        ref={player}
        url={url}
        width="100%"
        height="100%"
        playing={config.tocando}
        onProgress={progresso}
        onEnded={() => setConfig({ ...config, tocando: false })}
        controls={false}
        muted={config.mudo}
        playsinline
        config={{
          file: {
            forceHLS: !isIOS && unidadeDeAprendizagem ? true : false,
            forceVideo: !unidadeDeAprendizagem ? true : false,
            tracks: tracks,
            hlsOptions: {
              xhrSetup: (xhr: XMLHttpRequest) => {
                xhr.withCredentials = true;
              },
            },
            ...(unidadeDeAprendizagem
              ? { attributes: { crossOrigin: 'use-credentials' } }
              : {}),
          },
        }}
      />
      <div
        className="tl-opacity position-absolute top-0 start-0 bottom-0 end-0"
        style={{
          background: 'rgba(0,0,0, 0.5)',
          opacity: controles || !config.tocando ? 1 : 0,
        }}
        onMouseMove={() => {
          if (!controles) {
            setControles(true);

            setTimeout(() => setControles(false), 5000);
          }
        }}
      >
        <div
          className="position-absolute top-0 bottom-0 start-0 end-0 d-flex justify-content-center align-items-center"
          onClick={() => {
            if (controles || !config.tocando) {
              tocar();
            } else {
              if (!controles) {
                setControles(true);

                setTimeout(() => setControles(false), 5000);
              }
            }
          }}
        >
          {config.mudo && (
            <div className="bg-gray rounded-3 p-3 m-4 backdrop-filter-blur">
              <p className="text-center text-white mb-0 fs-3">
                Para habilitar o som da aula, clique no ícone de alto falante (
                <Icone nome="volume-off" className="text-danger" />) logo
                abaixo.
              </p>
            </div>
          )}
        </div>
        <div
          className="position-absolute top-0 start-0 d-flex align-items-start ps-3"
          style={{ right: 70 }}
        >
          {aoFechar && (
            <button
              type="button"
              className="btn btn-transparent border-0 text-white fs-1"
              onClick={aoFechar}
            >
              <Icone nome="x-lg" />
            </button>
          )}
          <h1 className="text-white fw-lighter mb-0 mt-2">{titulo}</h1>
        </div>
        <div className="position-absolute top-0 end-0 d-flex align-items-center">
          <button
            type="button"
            className="btn btn-transparent border-0 text-white fs-1 me-3"
            onClick={telaCheia}
          >
            {config.telaCheia ? (
              <Icone nome="fullscreen-exit" />
            ) : (
              <Icone nome="arrows-fullscreen" />
            )}
          </button>
        </div>
        <div className="w-100 position-absolute bottom-0 start-0 d-flex flex-column justify-content-center align-items-center">
          <div className="w-100 py-2 px-4 d-flex justify-content-between">
            <span className="text-white">
              {config.segundosTocados.segundosParaTempo()}
            </span>
            <span className="text-white">
              {player.current?.getDuration()?.segundosParaTempo()}
            </span>
          </div>
          <div
            className="progress w-100 rounded-0 cursor-pointer tl-height"
            onMouseLeave={mouseSaiuDaLinhaDoTempo}
            onMouseMove={mouseSobreLinhaDoTempo}
            onMouseUpCapture={cliqueSobreLinhaDoTempo}
            style={{
              background: 'rgba(255,255,255, 0.3)',
              height: porcentagemLinhaDoTempo != 0 || isMobile ? '15px' : '5px',
            }}
          >
            <div
              className="progress-bar"
              style={{
                width: `${
                  porcentagemLinhaDoTempo == 0
                    ? (config.segundosTocados * 100) /
                      (player.current?.getDuration() || 1)
                    : porcentagemLinhaDoTempo
                }%`,
              }}
            />
          </div>
          <div className="w-100 d-flex align-items-center justify-content-between">
            <div style={{ flex: 1 }} className="d-flex justify-content-start gap-3">
              <button
                type="button"
                className="btn btn-transparent border-0 text-white"
                onClick={mutar}
              >
                {!config.mudo ? (
                  <Icone nome="volume-up" className="fs-1" />
                ) : (
                  <Icone nome="volume-off" className="fs-1" />
                )}
              </button>
            </div>
            <div style={{ flex: 1 }} className="d-flex justify-content-center gap-2">
              {!isMobile && (
                <button
                  type="button"
                  className="btn btn-transparent border-0 text-white"
                  onClick={voltar10}
                >
                  <Icone nome="rewind" className="fs-1" />
                </button>
              )}
              <button
                type="button"
                className="btn btn-transparent border-0 text-white"
                onClick={tocar}
              >
                {config.tocando ? (
                  <Icone nome="pause" className="fs-1" />
                ) : (
                  <Icone nome="play" className="fs-1" />
                )}
              </button>
              {!isMobile && (
                <button
                  type="button"
                  className="btn btn-transparent border-0 text-white"
                  onClick={avancar10}
                >
                  <Icone nome="fast-forward" className="fs-1" />
                </button>
              )}
            </div>
            <div style={{ flex: 1 }} className="d-flex justify-content-end gap-2">
              {unidadeDeAprendizagem && (
                <button
                  type="button"
                  className="btn btn-transparent border-0 text-white d-flex jusityf-content-center align-items-center gap-3"
                  onClick={() => setOffcanvas(true)}
                >
                  <span className="fs-5">Materiais</span>
                  <Icone nome="journal-text" className="fs-1" />
                </button>
              )}
              {livro && (
                <a
                  href={livro}
                  className="btn btn-transparent border-0 text-white d-flex jusityf-content-center align-items-center gap-3"
                  target="_blank"
                  rel="noreferrer"
                >
                  <span className="fs-5">Livro</span>
                  <Icone nome="journal-text" className="fs-1" />
                </a>
              )}
              {textTracks.length > 0 && (
                <div className="dropdown d-inline-block">
                  <button
                    type="button"
                    className="btn btn-transparent border-0 text-white"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    <Icone nome="badge-cc" className="fs-1" />
                  </button>
                  <ul className="dropdown-menu">
                    {textTracks.map((track) => (
                      <li key={track.label}>
                        <a
                          className={`dropdown-item ${
                            track.mode === 'showing' ? 'active' : ''
                          }`}
                          onClick={() => toggleTrack(track)}
                        >
                          {track.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {unidadeDeAprendizagem && (
        <OffCanvas
          titulo={unidadeDeAprendizagem.titulo}
          exibir={offcanvas}
          aoFechar={setOffcanvas}
        >
          <React.Fragment>
            {aba == 0 && (
              <React.Fragment>
                <p className="fs-2 fw-bold">Descrição da aula</p>
                <div
                  dangerouslySetInnerHTML={{
                    __html: draftToHtml(
                      unidadeDeAprendizagem.descricao as unknown as RawDraftContentState
                    ),
                  }}
                />
                <p className="fs-3 fw-bold">Competências adquiridas</p>
                <div
                  dangerouslySetInnerHTML={{
                    __html: draftToHtml(
                      unidadeDeAprendizagem.competencias as unknown as RawDraftContentState
                    ),
                  }}
                />
              </React.Fragment>
            )}
            {aba == 1 && (
              <Materiais unidadeDeAprendizagemId={unidadeDeAprendizagem.id} />
            )}
            {aba == 2 && (
              <Quiz unidadeDeAprendizagemId={unidadeDeAprendizagem.id} />
            )}
            {aba == 3 && (
              <DicasMais unidadeDeAprendizagemId={unidadeDeAprendizagem.id} />
            )}
            {aba == 4 && (
              <Bibliografias
                unidadeDeAprendizagemId={unidadeDeAprendizagem.id}
              />
            )}
          </React.Fragment>
          <div className="mt-5 pt-5"></div>
          <div className="position-fixed bottom-0 w-100 bg-light border-top mx-n3">
            <div className="d-flex justify-content-around">
              <button
                className="btn btn-transparent border-0"
                onClick={() => setAba(0)}
              >
                <Icone
                  nome="justify-left"
                  className={`${
                    aba == 0 ? 'text-primary' : 'text-muted'
                  } fs-3 d-block`}
                />
                Descrição
              </button>
              <button
                className="btn btn-transparent border-0"
                onClick={() => setAba(1)}
              >
                <Icone
                  nome="journal-bookmark-fill"
                  className={`${
                    aba == 1 ? 'text-primary' : 'text-muted'
                  } fs-3 d-block`}
                />
                Materiais
              </button>
              <button
                className="btn btn-transparent border-0"
                onClick={() => setAba(2)}
              >
                <Icone
                  nome="pencil"
                  className={`${
                    aba == 2 ? 'text-primary' : 'text-muted'
                  } fs-3 d-block`}
                />
                Quiz
              </button>
              <button
                className="btn btn-transparent border-0"
                onClick={() => setAba(3)}
              >
                <Icone
                  nome="plus-lg"
                  className={`${
                    aba == 3 ? 'text-primary' : 'text-muted'
                  } fs-3 d-block`}
                />
                Dicas+
              </button>
              <button
                className="btn btn-transparent border-0"
                onClick={() => setAba(4)}
              >
                <Icone
                  nome="book-half"
                  className={`${
                    aba == 4 ? 'text-primary' : 'text-muted'
                  } fs-3 d-block`}
                />
                Bibliografia
              </button>
            </div>
          </div>
        </OffCanvas>
      )}
    </div>
  );
};

export default Video;
